import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { listOrders, deleteOrder, listStaff, listSites, erpResend, reconcileSlips } from '../../api/endpoints';
import { ApiError, parseApiErrorMessage } from '../../api/client';
import { formatJST } from '../../utils/format';
import { toCsv, downloadCsv } from '../../utils/csv';
import type { OrderSummary, PaginatedResponse, Staff } from '../../types/api';

type TabKey = 'order' | 'estimate' | 'history' | 'estimate_history';

/** 伝票No.突合の結果。unavailable=ERP未接続(想定内) / error=サーバーエラー(異常)。 */
type ReconcileState =
  | { kind: 'ok' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'error'; message: string };

// 1ウィンドウで取得する上限（全件ロードを避ける安全弁）
const WINDOW_CAP = 3000;
// ERP再送を許可する状態。ERPに「これから取り込まれる行」が残っていないものだけ。
//   erp_written(カクテル取込待ち) と completed(伝票成立済み) で再送すると、
//   カクテルが新旧どちらも取り込み1伝票の明細が倍になる。
//   erp_error はカクテルが弾いた行で再処理されないため、再送してよい。
//   erp_blocked は明細が多すぎて送っていない状態。ERPに行が無いので重複しない。
//   明細を減らして再送すれば通るため許可する。
//   erp_processing は書込み処理の最中。ここで押すと二重にERPへ入るため許可しない。
const RESENDABLE_STATUSES = ['erp_failed', 'erp_error', 'erp_skipped', 'erp_blocked'] as const;

/** 再送してよい状態か。判定はサーバー側でもERPを引き直して行う（最後の砦）。 */
function canResend(status?: string): boolean {
  return !status || (RESENDABLE_STATUSES as readonly string[]).includes(status);
}

/** 再送できない理由（ボタンの title に出す）。押せる状態なら undefined。 */
function resendBlockedReason(status?: string, slipNumber?: string): string | undefined {
  if (canResend(status)) return undefined;
  if (status === 'completed') {
    return slipNumber ? `伝票 ${slipNumber} で登録済みです` : '登録済みです';
  }
  if (status === 'erp_processing') {
    return 'ERPへの書込み処理中です。完了までお待ちください';
  }
  return 'カクテル取込待ちです。取込後に伝票No.が自動で表示されます';
}

const WINDOW_OPTIONS = [
  { days: 7, label: '直近7日' },
  { days: 30, label: '直近30日' },
  { days: 90, label: '直近90日' },
  { days: 0, label: '全期間' },
];

/** 直近 days 日の注文を取得（days=0 は全期間）。上限 WINDOW_CAP 件でバウンド。 */
async function fetchOrders(days: number): Promise<PaginatedResponse<OrderSummary>> {
  const first = await listOrders(1, 200, days);
  if (first.total <= first.items.length) return first;
  return listOrders(1, Math.min(first.total, WINDOW_CAP), days);
}

/** created_at（JST）が [from, to] の範囲内か判定。境界は両端含む。 */
function inDateRange(createdAt: string, from: string, to: string): boolean {
  if (!from && !to) return true;
  if (!createdAt) return false;
  // created_at の日付部分（YYYY-MM-DD）を JST で取り出す
  const normalized = createdAt.endsWith('Z') || /[+-]\d{2}(:\d{2})?$/.test(createdAt) ? createdAt : createdAt + '+09:00';
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return true;
  const ymd = d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' }); // "YYYY-MM-DD"
  if (from && ymd < from) return false;
  if (to && ymd > to) return false;
  return true;
}

export function OrdersPage() {
  const [windowDays, setWindowDays] = useState(7);
  const { data, loading, error, refetch } = useApi(() => fetchOrders(windowDays), [windowDays]);
  const [reconciling, setReconciling] = useState(false);
  // 突合の失敗は2種類ある。ERP未接続は 200 + {ok:false} で返るため例外にならず、
  // catch を直しただけでは拾えない。運用上ふつうに起きるものと異常を分けて出す。
  const [reconcileState, setReconcileState] = useState<ReconcileState>({ kind: 'ok' });
  const [tab, setTab] = useState<TabKey>('order');
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [siteNameMap, setSiteNameMap] = useState<Record<string, string>>({});

  // 受注伝票No.の突合（ERP書込みタブを開いた時＝マウント時に一度、手動でも実行可）
  async function runReconcile() {
    setReconciling(true);
    try {
      const r = await reconcileSlips(windowDays > 0 ? windowDays : 7);
      if (!r.ok) {
        setReconcileState({ kind: 'unavailable', message: r.error || 'ERPに接続できません' });
        return;
      }
      setReconcileState({ kind: 'ok' });
      if ((r.completed?.length || 0) + (r.errors?.length || 0) + (r.reverted?.length || 0) > 0) refetch();
    } catch (e) {
      // 握り潰すと、突合が止まっていることに誰も気付けない（2026-09-06 に23時間）。
      console.error('伝票No.の突合に失敗', e);
      setReconcileState({ kind: 'error', message: (e as Error).message });
    } finally { setReconciling(false); }
  }

  useEffect(() => {
    void runReconcile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── フィルター状態 ──
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [overrideOnly, setOverrideOnly] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await listStaff();
        setStaffList(res.items);
      } catch { /* silent */ }
    })();
  }, []);

  // 現場名をマスタから取得（site_name が空の注文用）
  useEffect(() => {
    if (!data) return;
    const missing = (data.items ?? []).filter((o) => o.site_code && !o.site_name && !siteNameMap[o.site_code]);
    if (missing.length === 0) return;
    const customerCodes = [...new Set(missing.map((o) => o.customer_code))];
    void (async () => {
      const map: Record<string, string> = { ...siteNameMap };
      for (const tokcd of customerCodes) {
        try {
          const res = await listSites({ tokcd });
          for (const s of res.items) {
            map[s.code] = s.name || s.abbr || s.code;
          }
        } catch { /* silent */ }
      }
      setSiteNameMap(map);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function handleDelete(orderId: string) {
    if (!confirm(`注文 ${orderId} を削除しますか？`)) return;
    try {
      await deleteOrder(orderId);
      refetch();
    } catch (e) {
      alert(`削除失敗: ${(e as Error).message}`);
    }
  }

  async function handleErpResend(orderId: string) {
    if (!confirm('ERP書込みを再送信しますか？')) return;
    try {
      const res = await erpResend(orderId);
      alert(res.erp.status === 'erp_failed'
        ? 'ERP書込みに失敗しました。'
        : 'ERPへ送信しました。伝票No.はカクテルが採番後に自動で表示されます。');
      refetch();
    } catch (e) {
      // 409: サーバーがERPを引き直した結果、送ると伝票が重複する状態だった。
      // 画面の状態が古かったということなので、理由を見せて一覧を最新化する。
      if (e instanceof ApiError && e.status === 409) {
        alert(parseApiErrorMessage(e) ?? '現在の状態では再送できません。');
        refetch();
        return;
      }
      alert(`ERP再送信失敗: ${(e as Error).message}`);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const allItems = data.items ?? [];

  // ── 表示用ヘルパー（テーブル・CSV で共用） ──
  const resolveStaffName = (raw?: string): string => {
    if (!raw) return '';
    const name = /^\d+$/.test(raw) ? staffList.find((s) => s.code === raw)?.name : undefined;
    return name || raw;
  };
  const resolveSiteName = (o: OrderSummary): string =>
    o.site_code ? (o.site_abbr || o.site_name || siteNameMap[o.site_code] || o.site_code) : '';
  const resolveWarehouse = (o: OrderSummary): string => o.warehouse_name || o.warehouse_code || '';
  // CSV用。画面のバッジと同じ言葉にする（未対応の状態を「未送信」に丸めると、
  // エラーや書込み不可の注文がCSV上は送信前と区別できなくなる）。
  const statusText = (s?: string): string => {
    switch (s) {
      case 'completed': return '書込み完了';
      case 'erp_written': return '伝票No.未取得';
      case 'erp_failed': return '書込み失敗';
      case 'erp_error': return 'ERP検証エラー';
      case 'erp_blocked': return '書込み不可';
      case 'erp_processing': return '書込み処理中';
      default: return '未送信';
    }
  };

  // 倉庫フィルタの選択肢（全データから distinct）
  const warehouseOptions = [...new Set(allItems.map(resolveWarehouse).filter(Boolean))].sort();

  // ── 横断フィルター（期間・倉庫・名称上書き）。タブとは AND で合成 ──
  const matchesFilters = (o: OrderSummary): boolean =>
    inDateRange(o.created_at, dateFrom, dateTo) &&
    (!warehouse || resolveWarehouse(o) === warehouse) &&
    (!overrideOnly || !!o.has_name_override);
  const filterActive = !!(dateFrom || dateTo || warehouse || overrideOnly);
  const visible = allItems.filter(matchesFilters);

  // 見積は estimate / estimate_maker（メーカー見積）の両方を含める。
  const estimates = visible.filter((o) => (o.doc_type ?? '').startsWith('estimate'));
  const orders = visible.filter((o) => !(o.doc_type ?? '').startsWith('estimate'));
  const isDone = (o: OrderSummary) => o.erp_status === 'completed';
  const ordersPending = orders.filter((o) => !isDone(o));
  const ordersDone = orders.filter((o) => isDone(o));
  const estimatesPending = estimates.filter((o) => !isDone(o));
  const estimatesDone = estimates.filter((o) => isDone(o));

  const filteredMap: Record<TabKey, OrderSummary[]> = {
    order: ordersPending,
    estimate: estimatesPending,
    history: ordersDone,
    estimate_history: estimatesDone,
  };
  const filtered = filteredMap[tab];

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'order', label: `注文書 (${ordersPending.length})` },
    { key: 'estimate', label: `見積 (${estimatesPending.length})` },
    { key: 'history', label: `注文履歴 (${ordersDone.length})` },
    { key: 'estimate_history', label: `見積履歴 (${estimatesDone.length})` },
  ];

  function clearFilters() {
    setDateFrom('');
    setDateTo('');
    setWarehouse('');
    setOverrideOnly(false);
  }

  const tabLabel: Record<TabKey, string> = {
    order: '注文書',
    estimate: '見積',
    history: '注文履歴',
    estimate_history: '見積履歴',
  };

  function handleCsvExport() {
    // 伝票数・マスタ照合明細数・特殊品明細数は三興バルブ様の要望(No.77)。照合/特殊品は
    // 明細行だけを数える（見積の備考・小計・中計は含まない）ので、合計は明細数と一致しないことがある。
    const headers = ['書込み日時', 'ステータス', '伝票管理No.', '種別', '得意先コード', '得意先名', '現場', '入力担当者', '倉庫', '明細数',
      '伝票数', 'マスタ照合明細数', '特殊品明細数'];
    const rows = filtered.map((o) => {
      const erpNo = o.erp_slip_number || ((o.doc_type ?? '').startsWith('estimate') ? o.erp_estimate_number : o.erp_order_number) || '';
      return [
        formatJST(o.created_at),
        statusText(o.erp_status),
        erpNo,
        (o.doc_type ?? '').startsWith('estimate') ? '見積' : '注文',
        o.customer_code,
        o.customer_abbr || o.customer_name,
        resolveSiteName(o),
        resolveStaffName(o.input_staff_code),
        resolveWarehouse(o),
        o.items_count,
        o.slip_count ?? 1,
        o.matched_count ?? '',
        o.spot_count ?? '',
      ];
    });
    const stamp = formatJST(new Date().toISOString()).replace(/[/: ]/g, '').replace(/-/g, '');
    downloadCsv(`orders_${tabLabel[tab]}_${stamp}.csv`, toCsv(headers, rows));
  }

  return (
    <Panel title="ERP書込み">
      {/* 突合の失敗。黙って捨てると、伝票No.が更新されていないことに誰も気付けない。
          ERP未接続(運用上ふつうに起きる)とサーバーエラー(異常)は見た目を分ける。
          同じ赤で出すと日常化して読まれなくなるため。 */}
      {reconcileState.kind !== 'ok' && (
        <div
          role="status"
          className={`mb-4 flex items-start gap-3 rounded border px-3 py-2 text-sm ${
            reconcileState.kind === 'error'
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-slate-300 bg-slate-50 text-slate-600'
          }`}
        >
          <span aria-hidden="true">{reconcileState.kind === 'error' ? '⚠' : 'ℹ'}</span>
          <span className="flex-1">
            {reconcileState.kind === 'error'
              ? '伝票No.の状態を更新できませんでした。表示中の状態は実際と異なる可能性があります。'
              : `ERPに接続できないため、伝票No.の状態を更新できていません（${reconcileState.message}）。`}
          </span>
          <Button variant="ghost" className="shrink-0 px-2 py-0.5 text-xs"
                  disabled={reconciling} onClick={() => void runReconcile()}>
            {reconciling ? '再試行中...' : '再試行'}
          </Button>
        </div>
      )}

      {/* タブ */}
      <div className="flex gap-1 mb-4 border-b">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* フィルターバー */}
      <div className="flex flex-wrap items-end gap-3 mb-3 p-3 bg-slate-50 rounded">
        <label className="flex flex-col text-xs text-slate-500">
          表示期間
          <select
            className="mt-1 px-2 py-1 border border-slate-300 rounded text-sm text-slate-800 bg-white"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            {WINDOW_OPTIONS.map((w) => (
              <option key={w.days} value={w.days}>{w.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          書込み日（開始）
          <input
            type="date"
            className="mt-1 px-2 py-1 border border-slate-300 rounded text-sm text-slate-800"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          書込み日（終了）
          <input
            type="date"
            className="mt-1 px-2 py-1 border border-slate-300 rounded text-sm text-slate-800"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-xs text-slate-500">
          倉庫
          <select
            className="mt-1 px-2 py-1 border border-slate-300 rounded text-sm text-slate-800 bg-white"
            value={warehouse}
            onChange={(e) => setWarehouse(e.target.value)}
          >
            <option value="">すべて</option>
            {warehouseOptions.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 pb-1">
          <input
            type="checkbox"
            checked={overrideOnly}
            onChange={(e) => setOverrideOnly(e.target.checked)}
          />
          商品名（OCR名）に切替えた注文のみ
        </label>
        {filterActive && (
          <Button variant="ghost" onClick={clearFilters}>フィルタークリア</Button>
        )}
        <div className="ml-auto flex gap-2">
          {tab !== 'history' && tab !== 'estimate_history' && (
            <Button variant="ghost" onClick={runReconcile} disabled={reconciling}>
              {reconciling ? '伝票No.確認中...' : '伝票No.更新'}
            </Button>
          )}
          <Button variant="secondary" onClick={handleCsvExport} disabled={filtered.length === 0}>
            CSV出力
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted mb-2">{filtered.length} 件</p>
      <div className="overflow-auto">
        <table className="whitespace-nowrap">
              <thead>
                <tr>
                  <th>書込み日時</th>
                  <th>ステータス</th>
                  <th>伝票管理No.</th>
                  <th>種別</th>
                  <th>得意先名</th>
                  <th>現場</th>
                  <th>倉庫</th>
                  <th>入力担当者</th>
                  <th>明細数</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const erpNo = o.erp_slip_number || ((o.doc_type ?? '').startsWith('estimate') ? o.erp_estimate_number : o.erp_order_number);
                  const statusBadge = (() => {
                    switch (o.erp_status) {
                      case 'completed':
                        return <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs">書込み完了</span>;
                      case 'erp_written':
                        return <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">伝票No.未取得</span>;
                      case 'erp_failed':
                        return <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">書込み失敗</span>;
                      case 'erp_error': {
                        // カクテルの ERRCM（内容文）があれば理由として表示。汎用文言のみなら省略。
                        const reason = o.erp_error && o.erp_error !== 'ERP検証エラー' ? o.erp_error : '';
                        return (
                          <span className="inline-flex flex-col items-start gap-0.5">
                            <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs" title={reason || 'ERP検証エラー'}>ERP検証エラー</span>
                            {reason && (
                              <span className="text-[11px] leading-tight text-red-600 max-w-[18rem] break-words" title={reason}>{reason}</span>
                            )}
                          </span>
                        );
                      }
                      case 'erp_processing':
                        // 注文はCosmosへ先に保存され、そのあとERPへ書き込まれる。
                        // その間を「未送信」に見せると再送されて二重書込みになる。
                        return <span className="inline-block px-2 py-0.5 rounded bg-sky-100 text-sky-800 text-xs">書込み処理中</span>;
                      case 'erp_blocked':
                        // 明細が多すぎてERPへ送っていない。理由（行数）は erp_error に入る。
                        return (
                          <span className="inline-flex flex-col items-start gap-0.5">
                            <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs" title={o.erp_error || '明細が多すぎます'}>書込み不可</span>
                            {o.erp_error && (
                              <span className="text-[11px] leading-tight text-red-600 max-w-[18rem] break-words" title={o.erp_error}>{o.erp_error}</span>
                            )}
                          </span>
                        );
                      case 'erp_skipped':
                        return <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">未送信</span>;
                      default:
                        return <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">未送信</span>;
                    }
                  })();
                  return (
                    <tr key={o.order_id}>
                      <td className="text-sm">{formatJST(o.created_at)}</td>
                      <td className="text-sm">{statusBadge}</td>
                      <td className="text-sm font-mono">{erpNo || '-'}</td>
                      <td className="text-sm">
                        {(o.doc_type ?? '').startsWith('estimate') ? (
                          <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800">見積</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800">注文</span>
                        )}
                      </td>
                      <td className="text-sm">{o.customer_abbr || o.customer_name}</td>
                      <td className="text-sm">{resolveSiteName(o) || '-'}</td>
                      <td className="text-sm">{resolveWarehouse(o) || '-'}</td>
                      <td className="text-sm">{resolveStaffName(o.input_staff_code) || '-'}</td>
                      <td className="text-sm text-center">{o.items_count} 件</td>
                      <td className="text-sm whitespace-nowrap">
                        {o.has_name_override && (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-xs" title="明細の商品名をマスタ名からOCR名に切り替えています">商品名（OCR名）に切替</span>
                        )}
                      </td>
                      <td className="flex gap-1">
                        <Link to={`/orders/${o.order_id}`}>
                          <Button variant="secondary">詳細</Button>
                        </Link>
                        {tab !== 'history' && tab !== 'estimate_history' && (
                          <Button
                            variant="primary"
                            disabled={!canResend(o.erp_status)}
                            title={resendBlockedReason(o.erp_status, erpNo)}
                            onClick={() => handleErpResend(o.order_id)}
                          >
                            ERP送信
                          </Button>
                        )}
                        {tab !== 'history' && tab !== 'estimate_history' && (
                          <Button variant="danger" onClick={() => handleDelete(o.order_id)}>
                            削除
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
    </Panel>
  );
}
